import React, { useState, useMemo, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Joi from 'joi'
import toast from 'react-hot-toast'

import countries from 'all-countries-and-cities-json'

import Modal from '../../../components/Modal'
import { PhoneNumberInput, StandardInput } from '../../../components/Input'
import { SingleSelect } from '../../../components/Select'

import { currencyList as allCurrencies } from '../../../layouts/currencies'

import apiClient from '../../../helpers/apiClient'

import { getFromLocalStorage } from '../../../utils/util'

import {
  setFirstName,
  setMiddleName,
  setLastName,
  setBasis,
  setCurrencies,
  setMinSalary,
  setMaxSalary,
  setEmail,
  setContactNumber,
  setCountry,
  setCity,
} from '../../../redux/feature/applicantSlice'

const ApplicantDetailsModal = () => {
  const applicantCVData = getFromLocalStorage('applicant-cv-data')
  const applicantData = getFromLocalStorage('applicant-data')
  const userId = useMemo(() => getFromLocalStorage('user_id'), [])

  const [loading, setLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})

  const [cityList, setCityList] = useState([])

  const validationSchema = useMemo(() => {
    return Joi.object({
      firstName: Joi.string().label('First Name').required(),
      middleName: Joi.string().label('Middle Name').allow(''),
      lastName: Joi.string().label('Last Name').required(),
      basis: Joi.object({
        value: Joi.string().required(),
        label: Joi.string().required(),
      })
        .label('Salary Basis')
        .required()
        .messages({
          'object.base': 'Salary Basis must be a valid selection.',
          'any.required': 'Salary Basis is required.',
        }),
      minSalary: Joi.number()
        .integer()
        .positive()
        .less(Joi.ref('maxSalary'))
        .label('Min Salary')
        .required()
        .messages({
          'number.base': 'Min Salary must be a valid number.',
          'number.less': 'Min Salary must be less than Max Salary.',
          'number.positive': 'Min Salary must be a positive number.',
          'any.required': 'Min Salary is required.',
        }),

      maxSalary: Joi.number()
        .integer()
        .positive()
        .label('Max Salary')
        .required()
        .messages({
          'number.base': 'Max Salary must be a valid number.',
          'number.positive': 'Max Salary must be a positive number.',
          'any.required': 'Max Salary is required.',
        }),
      currencies: Joi.object({
        value: Joi.string().required(),
        label: Joi.string().required(),
      })
        .label('Currency')
        .required()
        .messages({
          'object.base': 'Currency must be a valid selection.',
          'any.required': 'Currency is required.',
        }),
      email: Joi.string()
        .label('Email')
        .email({ tlds: { allow: false } })
        .required(),

      contactNumber: Joi.string().label('Contact Number').required(),
      country: Joi.object({
        value: Joi.string().required(),
        label: Joi.string().required(),
      })
        .label('Country')
        .required()
        .messages({
          'object.base': 'Country must be a valid selection.',
          'any.required': 'Country is required.',
        }),
      city: Joi.object({
        value: Joi.string().required(),
        label: Joi.string().required(),
      })
        .label('City')
        .required()
        .messages({
          'object.base': 'City must be a valid selection.',
          'any.required': 'City is required.',
        }),
    })
  }, [])

  const {
    firstName,
    middleName,
    lastName,
    basis,
    currencies,
    minSalary,
    maxSalary,
    email,
    contactNumber,
    country,
    city,
  } = useSelector((state) => state.applicant)

  const dispatch = useDispatch()

  const salaryBasisList = useMemo(
    () => [
      {
        value: 'Per Month',
        label: 'Per Month',
      },
      {
        value: 'Per Year',
        label: 'Per Year',
      },
    ],
    []
  )
  const countryList = useMemo(
    () => Object.keys(countries).map((e) => ({ value: e, label: e })),
    []
  )

  const currencyList = useMemo(
    () =>
      Object.entries(allCurrencies[0]).map((e) => ({
        value: e[0],
        label: e[0],
      })),
    []
  )

  const handleSelectSalaryBasis = (e) => dispatch(setBasis(e))
  const handleSelectCurrency = (e) => dispatch(setCurrencies(e))
  const handleSelectCity = (e) => dispatch(setCity(e))

  const handleSelectCountry = (e) => {
    dispatch(setCountry(e))

    const cities = countries[e.value]?.map((e) => ({
      label: e,
      value: e,
    }))

    setCityList(cities)

    dispatch(setCity(cities[0]))
  }

  const handleSaveApplicantDetails = () => {
    try {
      setLoading(true)
      const formData = {
        firstName,
        middleName,
        lastName,
        basis,
        currencies,
        minSalary,
        maxSalary,
        email,
        contactNumber,
        country,
        city,
      }

      const result = validationSchema.validate(formData, { abortEarly: false })

      if (!result.error) {
        setFieldErrors({})
        const payload = {
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          salary_basis: basis?.value,
          currencies: currencies?.value,
          min_salary: minSalary,
          max_salary: maxSalary,
          email,
          phone_number: contactNumber,
          country: country?.value,
          city: city?.value,
        }
        apiClient
          .put(`/applicant/${userId}/update`, payload)
          .then((resp) => {
            const result = resp.data.data

            const updatedApplicantCVData = {
              ...applicantCVData,
              first_name: result.first_name,
              middle_name: result.middle_name,
              last_name: result.last_name,
              salary_basis: payload.basis,
              currencies: payload.currencies,
              min_salary: payload.min_salary,
              max_salary: payload.max_salary,
              email: result.email,
              phone_number: result.phone_number,
            }

            localStorage.setItem(
              'applicant-cv-data',
              JSON.stringify(updatedApplicantCVData)
            )

            const updatedApplicantData = {
              ...applicantData,
              email: result.email,
              phone_number: result.phone_number,
              city: result.city,
              country: result.country,
            }

            localStorage.setItem(
              'applicant-data',
              JSON.stringify(updatedApplicantData)
            )

            toast.success(resp.data.message)
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message)
          })
      } else {
        const validationErrors = result.error.details.reduce((acc, curr) => {
          acc[curr.context.key] = curr.message.replace(/"/g, '')
          return acc
        }, {})
        setFieldErrors(validationErrors)
        toast.error('Please correct the validation errors before proceeding.')
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (applicantCVData) {
      dispatch(setFirstName(applicantCVData.first_name))
      dispatch(setMiddleName(applicantCVData.middle_name))
      dispatch(setLastName(applicantCVData.last_name))

      const applicantSalaryBasis = {
        value: applicantCVData?.salary_basis || 'Per Month',
        label: applicantCVData?.salary_basis || 'Per Month',
      }
      dispatch(setBasis(applicantSalaryBasis))

      const applicantCurrency = {
        value: applicantCVData?.currencies,
        label: applicantCVData?.currencies,
      }
      dispatch(setCurrencies(applicantCurrency))

      dispatch(setMinSalary(applicantCVData.min_salary))
      dispatch(setMaxSalary(applicantCVData.max_salary))
    }

    if (applicantData) {
      dispatch(setEmail(applicantData.email))
      dispatch(setContactNumber(applicantData.phone_number))

      const applicantCountry = {
        value: applicantData?.country,
        label: applicantData?.country,
      }

      const applicantCity = {
        value: applicantData?.city,
        label: applicantData?.city,
      }

      dispatch(setCountry(applicantCountry))
      dispatch(setCity(applicantCity))
    }
  }, [])

  return (
    <Modal
      title="Applicant Details"
      btnTitle={
        loading ? 'Updating applicant details' : 'Save Applicant Details'
      }
      btnFunc={handleSaveApplicantDetails}
    >
      <StyledApplicantDetailsForm>
        <InlineContainer>
          <StandardInput
            title="First Name"
            type="text"
            value={firstName}
            onChange={(e) => dispatch(setFirstName(e))}
            required
            wrapperClass={fieldErrors?.firstName && 'error'}
            subText={fieldErrors?.firstName}
          />

          <StandardInput
            title="Middle Name"
            type="text"
            value={middleName}
            onChange={(e) => dispatch(setMiddleName(e))}
            required
            wrapperClass={fieldErrors?.middleName && 'error'}
            subText={fieldErrors?.middleName}
          />

          <StandardInput
            title="Last Name"
            type="text"
            value={lastName}
            onChange={(e) => dispatch(setLastName(e))}
            required
            wrapperClass={fieldErrors?.lastName && 'error'}
            subText={fieldErrors?.lastName}
          />
        </InlineContainer>

        <InlineContainer>
          <StandardInput
            title="Email Address"
            type="text"
            value={email}
            onChange={(e) => dispatch(setEmail(e))}
            required
            wrapperClass={fieldErrors?.email && 'error'}
            subText={fieldErrors?.email}
          />
        </InlineContainer>

        <InlineContainer>
          <SingleSelect
            title="Salary Basis"
            options={salaryBasisList}
            selected={basis}
            placeholder="Select Salary Basis"
            onChange={handleSelectSalaryBasis}
            wrapperClass={fieldErrors.basis && 'error'}
            subText={fieldErrors.basis}
          />

          <SingleSelect
            title="Currency"
            options={currencyList}
            selected={currencies}
            placeholder="Select Currency"
            onChange={handleSelectCurrency}
            wrapperClass={fieldErrors.currencies && 'error'}
            subText={fieldErrors.currencies}
          />
        </InlineContainer>

        <InlineContainer>
          <StandardInput
            title="Min. Salary"
            type="number"
            value={minSalary}
            onChange={(e) => dispatch(setMinSalary(e))}
            required
            wrapperClass={fieldErrors?.minSalary && 'error'}
            subText={fieldErrors?.minSalary}
          />

          <StandardInput
            title="Max. Salary"
            type="number"
            value={maxSalary}
            onChange={(e) => dispatch(setMaxSalary(e))}
            required
            wrapperClass={fieldErrors?.maxSalary && 'error'}
            subText={fieldErrors?.maxSalary}
          />
        </InlineContainer>

        <InlineContainer>
          <SingleSelect
            title="Country"
            options={countryList}
            selected={country}
            placeholder="Select Country"
            onChange={handleSelectCountry}
            wrapperClass={fieldErrors.country && 'error'}
            subText={fieldErrors.country}
          />

          <SingleSelect
            title="City"
            options={cityList}
            selected={city}
            placeholder="Select City"
            onChange={handleSelectCity}
            wrapperClass={fieldErrors.city && 'error'}
            subText={fieldErrors.city}
          />
        </InlineContainer>

        <PhoneNumberInput
          title="Phone Number"
          country="ph"
          value={contactNumber}
          required
          wrapperClass={fieldErrors.contactNumber && 'error'}
          onChange={(value) => dispatch(setContactNumber(value))}
          subText={fieldErrors.contactNumber}
        />
      </StyledApplicantDetailsForm>
    </Modal>
  )
}

export default ApplicantDetailsModal

const StyledApplicantDetailsForm = styled.div`
  padding-top: 1rem;
`

const InlineContainer = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`
